<template>
  <Dialog
    v-model:visible="showModalRef"
    :close-on-escape="store.getters.dialogsCloseOnEscape"
    :dismissableMask="store.getters.dialogsDismissableMask"
    :modal="store.getters.dialogsModal"
    @after-hide="afterHide()"
    class="large-dialog"
  >
    <template #header>
      {{ title }}
      <i
        class="custom-close-dialog-button cursor-pointer p-2 pi pi-times"
        @click="closeModal()"
      ></i>
    </template>

    <div
      :class="[loadingForm ? 'flex' : 'hidden']"
      class="justify-content-center align-items-center h-full"
    >
      <i class="pi pi-spinner pi-spin" style="font-size: 2rem"></i>
    </div>

    <TabView
      @update:activeIndex="($event) => (tabIndex = $event)"
      class="tabview"
      :class="[
        loadingForm ? 'hidden' : 'block',
        justShowFormSix ? 'just-show-form-six' : '',
      ]"
      :scrollable="true"
    >
      <!-- Form one -->
      <TabPanel v-if="showFormOne">
        <template #header>
          <div :class="{ 'shake text-red-400': invalidForms[0] }">
            Configuración común
          </div>
        </template>

        <ReportFormOne
          ref="reportFormOneRef"
          @showCategoriesModal="showCategoriesModal"
          @esPublicoChanged="handleEsPublicoChanged"
        />
      </TabPanel>

      <!-- Form two -->
      <TabPanel v-if="showFormTwo">
        <template #header>
          <div :class="{ 'shake text-red-400': invalidForms[1] }">
            Configuración adicional
          </div>
        </template>

        <ReportFormTwo
          ref="reportFormTwoRef"
          :idInforme="register?.idInforme"
          @report-selected="onReportSelected"
          @show-upload-pbix-modal="showUploadPbixModal"
        />
      </TabPanel>

      <!-- Form three -->
      <TabPanel v-if="showFormThree">
        <template #header>
          <div :class="{ 'shake text-red-400': invalidForms[2] }">Roles</div>
        </template>

        <ReportFormThree
          ref="reportFormThreeRef"
          :idInforme="register?.idInforme"
          :alias="register?.alias"
          :esPublico="register?.esPublico"
          @changeHasRoles="handleChangeHasRoles"
        />
      </TabPanel>

      <!-- Form four -->
      <TabPanel v-if="showFormFour">
        <template #header>
          <div :class="{ 'shake text-red-400': invalidForms[3] }">Filtros</div>
        </template>

        <ReportFormFour ref="reportFormFourRef" />
      </TabPanel>

      <!-- Form five -->
      <TabPanel v-if="showFormFive">
        <template #header>
          <div :class="{ 'shake text-red-400': invalidForms[4] }">Gráficos</div>
        </template>

        <ReportFormFive ref="reportFormFiveRef" />
      </TabPanel>

      <!-- Form six -->
      <TabPanel v-if="showFormSix">
        <template #header>
          <div>Versiones</div>
        </template>

        <ReportFormSix
          ref="reportFormSixRef"
          :alias="register?.alias"
          :guidReportPowerBI="register.guidReportPowerBI"
        />
      </TabPanel>
    </TabView>

    <template #footer>
      <div class="col-12">
        <div class="flex justify-content-between flex-wrap gap-3">
          <Button
            v-if="
              !selectedImpersonation ||
              selectedImpersonation?.esAdministrador === true
            "
            :label="userRol == 'Usuario' ? 'Cerrar informe' : 'Cancelar'"
            severity="secondary"
            @click="closeModal()"
          />
          <Button
            v-if="userRol == 'Admin' && tabIndex != 5 && showSaveButton"
            :label="buttonTitle"
            :severity="
              buttonTitle == 'Cerrar informe' ? 'secondary' : 'primary'
            "
            @click="save"
            :loading="loading || loadingForm"
            loading-icon="pi pi-spinner"
          />
        </div>
      </div>
    </template>
  </Dialog>

  <!-- Categories modal -->
  <CategoriesDialogView ref="categoriesModalRef" @saved="loadCategory" />

  <!-- Upload PBIX modal -->
  <UploadPbixDialogView ref="uploadPbixModalRef" @uploaded="onUploadPbix" />
</template>

<script setup>
import { computed, ref, nextTick, onMounted, provide } from "vue";
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";
import { useStore } from "vuex";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";

import {
  reportsCreate,
  reportsUpdate,
  reportHasRoles,
} from "@/api/reports/reports.api.js";
import ReportFormOne from "@/pages/_root/reports-dialog/form-one/ReportFormOne.vue";
import ReportFormTwo from "@/pages/_root/reports-dialog/form-two/ReportFormTwo.vue";
import ReportFormThree from "@/pages/_root/reports-dialog/form-three/ReportFormThree.vue";
import ReportFormFour from "@/pages/_root/reports-dialog/form-four/ReportFormFour.vue";
import ReportFormFive from "@/pages/_root/reports-dialog/form-five/ReportFormFive.vue";
import ReportFormSix from "@/pages/_root/reports-dialog/form-six/ReportFormSix.vue";
import CategoriesDialogView from "@/pages/_root/categories-dialog/CategoriesDialogView.vue";
import UploadPbixDialogView from "@/pages/_root/upload-pbix-dialog/UploadPbixDialogView.vue";
import { reportTypes } from "@/composables/_commons/enums.js";
import {
  checkFormHasData,
  handleShowConfirm,
} from "@/composables/_commons/helper.js";
import { useAuthorization } from "@sttedra_ui/framework";

// #region Props, Emits
const emit = defineEmits(["saved"]);
// #endregion

// #region Params, Ref, Reactives, Computed
const loadingForm = ref(false);
const loading = ref(false);
const store = useStore();
const confirm = useConfirm();
const toast = useToast();
const showModalRef = ref(false);
const register = ref(null);
const reportFormOneRef = ref(null);
const reportFormTwoRef = ref(null);
const reportFormThreeRef = ref(null);
const reportFormFourRef = ref(null);
const reportFormFiveRef = ref(null);
const reportFormSixRef = ref(null);
const invalidForms = ref([false, false, false, false, false]);
const categoriesModalRef = ref(null);
const reportType = ref(null);
const reportIsPublic = ref(false);
const { modelRef: authorizationModelRef } = useAuthorization();
const userRol = authorizationModelRef.value.roles[0];
const selectedImpersonation = ref(null);
const uploadPbixModalRef = ref(null);
const tabIndex = ref(0);

const showFormOne = computed(() => {
  return (
    (userRol == "Admin" && !selectedImpersonation.value) ||
    selectedImpersonation.value?.esAdministrador === true
  );
});

const showFormTwo = computed(() => {
  return (
    (userRol == "Admin" && !selectedImpersonation.value) ||
    selectedImpersonation.value?.esAdministrador === true
  );
});

const showFormThree = computed(() => {
  return (
    reportType.value == reportTypes.PowerBI &&
    ((userRol == "Admin" && !selectedImpersonation.value) ||
      selectedImpersonation.value?.esAdministrador === true)
  );
});

const showFormFour = computed(() => {
  return (
    (reportType.value == reportTypes.Tabla ||
      reportType.value == reportTypes.Grafico ||
      reportType.value == reportTypes.Mapa) &&
    ((userRol == "Admin" && !selectedImpersonation.value) ||
      selectedImpersonation.value?.esAdministrador === true)
  );
});

const showFormFive = computed(() => {
  return (
    reportType.value == reportTypes.Grafico &&
    ((userRol == "Admin" && !selectedImpersonation.value) ||
      selectedImpersonation.value?.esAdministrador === true)
  );
});

const showFormSix = computed(() => {
  return (
    register.value?.guidReportPowerBI && reportType.value == reportTypes.PowerBI
  );
});

const justShowFormSix = computed(() => {
  if (
    !showFormOne.value &&
    !showFormTwo.value &&
    !showFormThree.value &&
    !showFormFour.value &&
    !showFormFive.value &&
    showFormSix.value
  ) {
    return true;
  }
});

const showedForms = computed(() => {
  const forms = [];

  const showForms = [
    {
      show: showFormOne.value,
      name: "FormOne",
    },
    {
      show: showFormTwo.value,
      name: "FormTwo",
    },
    {
      show: showFormThree.value,
      name: "FormThree",
    },
    {
      show: showFormFour.value,
      name: "FormFour",
    },
    {
      show: showFormFive.value,
      name: "FormFive",
    },
    {
      show: showFormSix.value,
      name: "FormSix",
    },
  ];

  showForms.forEach((form) => {
    if (form.show) {
      forms.push(form.name);
    }
  });

  return forms;
});

const showSaveButton = computed(() => {
  const currentForm = showedForms.value[tabIndex.value];

  if (currentForm === "FormSix") {
    return false;
  }

  return true;
});

const title = computed(() => {
  if (register.value?.id) {
    return `Editar informe ${register.value?.titulo}`;
  } else {
    return `Crear informe`;
  }
});

const buttonTitle = computed(() => {
  if (selectedImpersonation.value?.esAdministrador === false) {
    return "Cerrar informe";
  }

  if (register.value?.id) {
    return `Editar informe`;
  } else {
    return `Crear informe`;
  }
});
// #endregion

// #region Functions
async function getAditionalData(data, type) {
  loadingForm.value = true;

  // if the data object is not empty,
  if (Object.keys(data).length > 0) {
    data.categoria = data.categoria?.aliasCategoria;

    showModal(data, type);
  } else {
    showModal({}, type);
  }
}

async function showModal(data, type) {
  if (data?.idInforme) {
    data.id = data.idInforme;
  }

  reportType.value = type;
  showModalRef.value = true;

  register.value = null;

  if (data && Object.keys(data).length !== 0) {
    register.value = data;
    reportIsPublic.value = data.esPublico;
  }

  nextTick(async () => {
    if (
      (userRol == "Admin" && !selectedImpersonation.value) ||
      selectedImpersonation.value?.esAdministrador === true
    ) {
      if (reportFormOneRef.value) {
        await reportFormOneRef.value.loadForm(data, type);
      }

      if (reportFormTwoRef.value) {
        await reportFormTwoRef.value.loadForm(data, type);
      }

      if (reportFormThreeRef.value) {
        await reportFormThreeRef.value.loadForm(data, type);
      }

      if (type == reportTypes.Tabla || type == reportTypes.Grafico) {
        reportFormFourRef.value.loadFilters(data, type);
      }

      if (type == reportTypes.Grafico) {
        reportFormFiveRef.value.loadGraphs(data, type);
      }
    }

    loadingForm.value = false;
  });
}

function setQueryString(data) {
  if (
    (reportType.value == reportTypes.Tabla ||
      reportType.value == reportTypes.Grafico) &&
    register.value
  ) {
    data.IdFuenteDatos = data.fuenteDatos;

    const queryString = data.query;

    data.query = {
      // eslint-disable-next-line camelcase
      id_query: register.value.query?.idQuery,
      queryText: queryString,
      descripcion: queryString,
    };
  }
}

function checkResponse(response) {
  if (response.status == 200) {
    toast.add({
      severity: "success",
      summary: `Registro ${register.value?.id ? "editado" : "creado"}`,
      detail: `${
        register.value?.id ? "Edición" : "Creación"
      } realizada correctamente`,
      life: 3000,
    });

    if (!window.location.href.includes("localhost")) {
      showModalRef.value = false;
    }

    emit("saved", response.data);
  } else {
    const error = response.response?.data?.errors?.[0]?.message;

    if (!error) {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: `No se ha
      podido realizar la ${register.value?.id ? "edición" : "creación"}`,
        life: 3000,
      });
    } else {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: error,
        life: 3000,
      });
    }
  }

  loading.value = false;
}

async function save() {
  if (selectedImpersonation.value) {
    showModalRef.value = false;
    return;
  }

  invalidForms.value[0] = await reportFormOneRef.value.validateForm();
  invalidForms.value[1] = await reportFormTwoRef.value.validateForm();
  invalidForms.value[2] = await reportFormThreeRef.value?.checkRoles();

  if (invalidForms.value.some((invalid) => invalid)) {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Por favor, rellene los campos correctamente",
      life: 3000,
    });

    return;
  }

  loading.value = true;

  const registerId = register.value?.id;

  const reportFormOneValues = reportFormOneRef.value.getData();
  const reportFormTwoValues = reportFormTwoRef.value.getData(registerId);
  const reportFormThreeValues = reportFormThreeRef.value?.getData();
  const reportFormFourValues = reportFormFourRef.value?.getData(registerId);
  const reportFormFiveValues = reportFormFiveRef.value?.getData(registerId);

  const data = {
    uidInforme: register.value?.idInforme,
    tipoInforme: reportType.value,
    ...reportFormOneValues,
    ...reportFormTwoValues,
    RolesInforme: reportFormThreeValues,
    filtro: reportFormFourValues,
    graficos: reportFormFiveValues,
    tieneRoles: reportFormThreeRef.value?.hasRoles,
  };

  setQueryString(data);

  let response = null;

  if (registerId) {
    data.id = register.value.id;
    response = await reportsUpdate(data);
  } else {
    response = await reportsCreate(data);
  }

  checkResponse(response);
}

function showCategoriesModal() {
  categoriesModalRef.value.showModal(null, true);
}

function showUploadPbixModal() {
  uploadPbixModalRef.value.showModal();
}

function onUploadPbix(pbixFileUid) {
  reportFormTwoRef.value.loadPbixUid(pbixFileUid);
}

// Load the category saved in the reports dialog
function loadCategory(category) {
  reportFormOneRef.value.loadCategory(category);
}

function afterHide() {
  tabIndex.value = 0;
  invalidForms.value = [false, false, false, false, false];
}

function closeModal() {
  if (loading.value || userRol != "Admin" || selectedImpersonation.value) {
    showModalRef.value = false;
    return;
  }

  const registerId = register.value?.id;

  const reportFormThreeValues = reportFormThreeRef.value?.roles;
  const reportFormFourValues = reportFormFourRef.value?.getData(registerId);

  let showConfirm = false;

  if (
    checkFormHasData(reportFormOneRef.value.getData()) ||
    checkFormHasData(reportFormTwoRef.value.getData())
  ) {
    showConfirm = true;
  }

  if (reportFormThreeValues && reportFormThreeValues.length > 0) {
    showConfirm = true;
  }

  if (reportFormFourValues && reportFormFourValues.length > 0) {
    showConfirm = true;
  }

  if (showConfirm) {
    handleShowConfirm(confirm, showModalRef);
  } else {
    showModalRef.value = false;
  }
}

function handleChangeHasRoles(hasRoles) {
  reportFormOneRef.value.disableEsPublicoField(hasRoles);
}

function handleEsPublicoChanged(value) {
  reportIsPublic.value = value;
  reportFormThreeRef.value?.disableHasRolesField(value);
}

async function onReportSelected(selectedUidInforme, selectedGuidWorkspace) {
  if (!reportIsPublic.value) {
    const response = await reportHasRoles({
      ReportId: selectedUidInforme,
      WorkspaceId: selectedGuidWorkspace,
    });

    if (response.status == 200) {
      let hasRoles = reportFormThreeRef.value?.hasRoles;

      if (hasRoles != response.data) {
        reportFormThreeRef.value.hasRoles = response.data;

        toast.add({
          severity: "info",
          summary: "Cambio en los roles",
          detail: `Se ha ${
            response.data ? "activado" : "desactivado"
          } la pestaña de roles`,
          life: 3000,
        });
      }
    }
  }
}

const providedFilters = computed(() => {
  return reportFormFourRef.value
    ? reportFormFourRef.value.getData(register.value?.id)
    : [];
});

provide('reportFilters', providedFilters);

// #endregion

// #region Effects, Hooks
onMounted(() => {
  const impersonationToken = localStorage.getItem("impersonationToken");

  if (impersonationToken) {
    selectedImpersonation.value = JSON.parse(impersonationToken);
  }
});
// #endregion

// #region Provide, Expose
defineExpose({
  getAditionalData,
  showModal,
});
// #endregion
</script>

<style lang="scss" scoped>
.tabview {
  :deep(.p-tabview-nav-container) {
    .p-tabview-nav-content {
      .p-tabview-nav {
        .p-tabview-header {
          text-wrap: nowrap;
        }
      }
    }
  }

  :deep(.p-tabview-panels) {
    padding-left: 0;
    padding-right: 0;
  }

  &.just-show-form-six {
    :deep(.p-tabview-nav-container) {
      display: none;
    }
  }
}

.shake {
  animation: shake 0.5s;

  @keyframes shake {
    0% {
      transform: translateX(0);
    }

    25% {
      transform: translateX(-3px);
    }

    50% {
      transform: translateX(3px);
    }

    75% {
      transform: translateX(-3px);
    }

    100% {
      transform: translateX(0);
    }
  }
}
</style>
